import { useEffect, useState, useRef } from 'react'
import {
  useTransition,
  animated,
  config as springConfig,
} from '@react-spring/web'

import stylesSheet from './infoLayer.module.scss'
import { MarkerData } from 'Types/markers'
import styled, { useTheme } from 'styled-components'
import { device } from '../breakpoints'
import { Link } from '../styles'
import { Button, Box } from '@mui/material'
import locales from 'locales/locales'

const getEmail = (publication: string | null) => {
  const email = {
    'tdg': 'olivier.bot@tdg.ch',
    '24heures': 'romain.michaud@24heures.ch',
    'bernerzeitung': 'bern@tamedia.ch',
    'derbund': 'bern@tamedia.ch',
    'tagesanzeiger': 'zuerich@tages-anzeiger.ch'
  }
  if (publication && publication in email) {
    return 'mailto:' + email[publication as 'tdg' | '24heures' | 'bernerzeitung' | 'derbund' | 'tagesanzeiger']
  } else {
    return 'mailto:interaktiv@tagesanzeiger.ch'
  }
}


const getDate = (ts: number, lang: string) => {
  try {
    return new Date(ts * 1000).toLocaleString(`${lang}-CH`, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch (err) {
    console.warn(err)
    return false
  }
}
const Openhours = ({ hours }: { hours: string }) => {
  const theme = useTheme()
  const dailyHours = JSON.parse(hours)
  const today = (new Date().getDay() + 6) % 7
  const weekdays: { [key: string]: string } = {
    lundi: 'Montag',
    mardi: 'Dienstag',
    mercredi: 'Mittwoch',
    jeudi: 'Donnerstag',
    vendredi: 'Freitag',
    samedi: 'Samstag',
    dimanche: 'Sonntag',
  }

  return (
    <table>
      <tbody>
        {dailyHours.map((h: string, i: number) => {
          const cols = h.split(': ')

          return (
            <tr
              key={`hours-${i}`}
              style={{ fontWeight: i === today ? '800' : '400' }}
            >
              <td style={{ paddingRight: 10 }}>
                {theme.lang === 'fr' ? cols[0] : weekdays[cols[0]]}
              </td>
              <td>
                {cols[1] === 'Fermé'
                  ? theme.lang === 'fr'
                    ? cols[1]
                    : 'Geschlossen'
                  : cols[1]}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export const InfoLayer = ({
  activeMarker,
  handleClose,
  publisher,
}: {
  activeMarker: MarkerData | null
  handleClose: () => void
  publisher: string | undefined
}) => {
  const [showInfoLayer, setShowInfoLayer] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const infoLayer = useRef(null)
  const wrapper = useRef(null)
  const theme = useTheme()

  const transitions = useTransition(showInfoLayer, {
    config: { ...springConfig.default, velocity: 0, mass: 0.1, tension: 450 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    onRest: () => {
      !showInfoLayer && handleClose()
    },
  })

  useEffect(() => {
    if (activeMarker) {
      setTimeout(() => {
        if (wrapper && wrapper?.current) {
          // wrapper.current.scrollIntoView({ behaviour: 'smooth' })
        }
      }, 0)
      setShowInfoLayer(true)
      setImageLoaded(false)
    } else {
      setShowInfoLayer(false)
    }
  }, [activeMarker])

  const categories: { [key: string]: string } = {
    SF: 'Street Food',
    CDF: 'Le coup de fourchette',
    GAT: 'Genève à table',
  }

  return transitions(
    (styles, item) =>
      item && (
        <Wrapper ref={wrapper}>
          <Container
            ref={infoLayer}
            style={{
              transform: styles.opacity.to(
                (v) => 'translate3d(0,' + (1 - v) * 100 + '%,0)'
              ),
            }}
          >
            {activeMarker && (
              <MenuContainer>
                <MenuInfos>
                  {theme.lang === 'fr'
                    ? categories[activeMarker.typeShort] ||
                    activeMarker.typeShort
                    : activeMarker.name}
                </MenuInfos>
                <CloseButton
                  onClick={() => {
                    setShowInfoLayer(false)
                  }}
                >
                  <Close />
                </CloseButton>
              </MenuContainer>
            )}

            <div className='ui vertical fitted segment info-footer'>
              <TextContainer>
                {activeMarker && (
                  <div>
                    <Title>{activeMarker.title}</Title>
                    <Lead>{activeMarker.lead}</Lead>
                    {activeMarker.ts && (
                      <Meta>
                        {locales.publishedOn[theme.lang]}{' '}
                        {getDate(activeMarker.ts, theme.lang)}
                      </Meta>
                    )}
                    <Infos>
                      {activeMarker.image && (
                        <ImageContainer>
                          <img
                            src={activeMarker.image}
                            alt={activeMarker.name}
                            style={imageLoaded ? {} : { opacity: 0 }}
                            onLoad={() => setImageLoaded(true)}
                          />
                        </ImageContainer>
                      )}
                      <Description>{activeMarker.description}</Description>
                      {activeMarker.story_id && (
                        <div className='ui vertical center aligned very fitted segment'>
                          <Button
                            color='buttonDark'
                            rel='noreferrer'
                            target='_blank'
                            href={`https://www.${theme.publisherDomain}.ch/${activeMarker.story_id
                              }?utm_source=interactive`}
                            variant='contained'
                            disableElevation
                            sx={{
                              '&:hover': {
                                color: '#fff',
                              },
                            }}
                          >
                            <ContinueReading />{' '}
                            {locales.continueReading[theme.lang]}
                          </Button>
                        </div>
                      )}

                      <InfoBox>
                        <Box mt={2} p={2}>
                          {activeMarker.address && (
                            <div>
                              <InfoTitle>
                                {locales.infoAddress[theme.lang]}
                              </InfoTitle>
                              <div>{activeMarker.name}</div>
                              <div>{activeMarker.address}</div>
                            </div>
                          )}

                          {activeMarker.openHours && (
                            <div className={stylesSheet['spacing-top']}>
                              <InfoTitle>
                                {locales.infoSchedules[theme.lang]}
                              </InfoTitle>
                              <div style={{ marginBottom: '1em' }}>
                                <i>{locales.infoSchedulesNote[theme.lang]}</i>
                              </div>
                              <Openhours hours={activeMarker.openHours} />
                            </div>
                          )}
                        </Box>
                      </InfoBox>
                    </Infos>
                  </div>
                )}
              </TextContainer>
            </div>

            <TextContainerBorder>
              <TextContainer>
                <div className={stylesSheet['filters-group']}>
                  <div className={stylesSheet['filters-group-title']}>
                    <InlineFilter>
                      {
                        activeMarker?.filters.find((m) => m.name === 'Cuisine')
                          ?.value
                      }
                    </InlineFilter>
                    <InlineFilter>
                      {
                        activeMarker?.filters.find(
                          (m) => m.name === 'Catégorie'
                        )?.value
                      }
                    </InlineFilter>
                    {!['bernerzeitung', 'derbund'].includes(theme.publisherDomain) &&
                      <InlineFilter>
                        <>
                          {new Array(
                            activeMarker?.filters.find(
                              (m) => m.name === 'Prix'
                            )?.value
                          )
                            .fill(0)
                            .map((coin, i) => (
                              <span key={`coin-${i}`}>$</span>
                            ))}
                        </>
                      </InlineFilter>
                    }
                  </div>
                </div>
              </TextContainer>
            </TextContainerBorder>

            <InfoFooter>
              <InfoFooterContent>
                <Link
                  href={
                    getEmail(theme.publisherDomain)
                  }
                >
                  {locales.readerUpdate[theme.lang]}
                </Link>
              </InfoFooterContent>
            </InfoFooter>
          </Container>
          <Mask
            style={{
              opacity: styles.opacity,
            }}
          />
        </Wrapper>
      )
  )
}

const Mask = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
`

const InlineFilter = styled.div`
  display: inline-block;
  padding: 0.65em 1.5rem;
  background: ${(props) => props.theme.greyColor};
  color: ${(props) => props.theme.textColor};
  border-radius: 50px;
  margin-right: 1rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;

  &:last-child {
    margin-right: 0;
  }
`

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 20px 20px 0px 0px;
  @media ${device.tablet} {
    width: 50%;
  }
`
const Container = styled(animated.div)`
  z-index: 999;
  position: sticky;
  transform: translate3d(0, 0, 0);
  left: 0px;
  width: 100%;
  overflow-y: scroll;
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.085);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    top: 10vh;
    height: 90vh;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    top: 0;
    height: 100vh;
  }

  overscroll-behavior-y: none;
  scroll-behavior: smooth;

  .text {
    color: ${(props) => props.theme.textColor};
  }

  .spacing-top {
    margin-top: 0.5em;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const Infos = styled.div`
  margin-top: 1rem;
  font-size: 1.5rem;
  line-height: 1.5;
  color: ${(props) => props.theme.textColor};

  img {
    max-width: 100%;
    margin: auto;
  }
`

const InfoFooter = styled.div`
  height: 10vh;
  display: flex;
  align-items: center;
`

const InfoFooterContent = styled.div`
  width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
`

const InfoTitle = styled.div`
  font-weight: 800;
`

const CloseButton = styled.div`
  background-color: var(--text-color);
  margin-left: auto;
  height: 60px;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 10vh;
  position: absolute;
  right: 0;
  top: 0;

  &:after {
    content: '';
    margin-top: 75%;
  }
`

const Close = styled.div`
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  opacity: 0.75;

  &:before,
  &:after {
    left: 15px;
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
    background-color: ${(props) =>
    props.theme.colorMode === 'dark' ? '#000000' : '#ffffff'};
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
`

const MenuContainer = styled(animated.div)`
  height: 60px;
  font-weight: 100;
  user-select: none;
  background-color: ${(props) => props.theme.plainColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  position: sticky;
  transform: translate3d(0, 0, 0);
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
`

const MenuInfos = styled.div`
  color: ${(props) => props.theme.mainColor};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  line-height: 1.5;
  padding-right: 2rem;
  width: calc(100% - 20vh -1em);
  float: left;
  height: 100%;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.5rem;
  max-width: 600px;
  padding: 0 2rem;
  margin: auto;
  width: 100%;
`

const Title = styled.div`
  font-family: var(--font-source);
  font-weight: 600;
  line-height: 1;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 3em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 4em;
  }
`

const Lead = styled.div`
  font-family: var(--font-sans-serif);
  line-height: 1.35;
  margin-top: 1.25rem;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.85em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.85em;
  }
`

const Meta = styled.div`
  font-family: var(--font-sans-serif);
  line-height: 1.35;
  margin-top: 1em;
  margin-bottom: 2em;
  opacity: 0.55;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.3em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 1.3em;
  }
`

const Description = styled.div`
  position: relative;
  font-family: var(--font-serif);
  margin: 2rem 0;
  margin-bottom: 0;
  font-size: 1.15em;
  line-height: 1.65;

  &:after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0;
    height: 200px;
    width: 100%;
    background: linear-gradient(
      180deg,
      ${(props) =>
    props.theme.colorMode === 'dark'
      ? 'rgba(18, 18, 18, 0)'
      : 'rgba(255, 255, 255, 0)'}
        20%,
      ${(props) =>
    props.theme.colorMode === 'dark'
      ? 'rgba(18, 18, 18, 1)'
      : 'rgba(255, 255, 255, 1)'}
        90%
    );
  }
`

const TextContainerBorder = styled.div``

const TextContainer = styled.div`
  max-width: 600px;
  margin: auto;
  padding: 0 2rem;
`
const InfoBox = styled.div`
  background-color: ${(props) => props.theme.greyColor};
`

const ContinueReading = styled.div`
  color: white;
  display: inline-block;
  display: flex;
  align-items: center;

  // &::before {
  //   font-weight: 700;
  //   display: inline-block;
  //   font-size: 0.75em;
  //   line-height: 1.25em;
  //   content: 'Abo';
  //   padding: 0.1em 0.5em 0.12em 0.5em;
  //   background-color: white;
  //   color: var(--text-color);
  //   margin-right: 0.65em;
  // }
`

const ImageContainer = styled.div`
  @keyframes placeholderShimmer {
    0% {
      background-position: -1200px 0;
    }
    100% {
      background-position: 1200px 0;
    }
  }
  height: 100%;
  width: 100%;
  aspect-ratio: 2/1;
  overflow: hidden;
  background-color: #ffffff;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.15) 15%,
    rgba(0, 0, 0, 0.08) 30%
  );
  background-size: 1200px 100%;
  animation: placeholderShimmer 2s linear;
  animation-iteration-count: infinite;
  img {
    margin-bottom: -10px;
  }
`
